import { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { 
  ArrowLeft,
  Smartphone, 
  Globe, 
  Workflow, 
  Brain,
  CheckCircle2,
  ExternalLink,
  ShoppingCart,
  Loader2,
  Calendar,
  Building2,
  Layers
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PortfolioDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [orderDialog, setOrderDialog] = useState(false);
  const [orderDescription, setOrderDescription] = useState("");
  const [ordering, setOrdering] = useState(false);

  const getCategoryIcon = (category) => {
    const icons = {
      mobile_app: Smartphone,
      web_app: Globe,
      automation: Workflow,
      ai_solution: Brain
    };
    return icons[category] || Globe;
  };

  const getCategoryColor = (category) => {
    const colors = {
      mobile_app: "#00d4aa",
      web_app: "#0ea5e9",
      automation: "#a855f7",
      ai_solution: "#f97316"
    };
    return colors[category] || "#00d4aa";
  };

  const getCategoryLabel = (category) => {
    const labels = {
      mobile_app: "Mobil Uygulama",
      web_app: "Web Uygulama",
      automation: "Otomasyon",
      ai_solution: "Yapay Zeka"
    };
    return labels[category] || category;
  };

  useEffect(() => {
    fetchProject();
  }, [id]);

  const fetchProject = async () => {
    try {
      const response = await axios.get(`${API}/portfolio/${id}`);
      setProject(response.data);
    } catch (error) {
      console.error("Error fetching project:", error);
      toast.error("Proje bulunamadı");
      navigate("/portfolyo");
    } finally {
      setLoading(false);
    }
  };

  const handleOrderClick = () => {
    if (!isAuthenticated) {
      toast.info("Sipariş vermek için giriş yapmalısınız");
      navigate("/giris");
      return;
    }
    setOrderDialog(true);
    setOrderDescription("");
  };

  const handleOrderSubmit = async () => {
    if (!project) return;

    setOrdering(true);
    try {
      await axios.post(`${API}/orders`, {
        product_id: null,
        product_name: `${project.title} benzeri proje`,
        project_type: project.category,
        description: orderDescription || `${project.title} projesine benzer bir proje istiyorum.`,
        features: project.technologies || [],
        price: "Teklif Bekleniyor"
      });

      toast.success("Siparişiniz başarıyla oluşturuldu!");
      setOrderDialog(false);
      navigate("/dashboard");
    } catch (error) {
      console.error("Error creating order:", error);
      toast.error("Sipariş oluşturulurken hata oluştu");
    } finally {
      setOrdering(false);
    }
  };

  if (loading) {
    return (
      <div className="pt-16 md:pt-20 min-h-screen bg-[#0a0a0f] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-[#00d4aa] animate-spin" />
      </div>
    );
  }

  if (!project) {
    return null;
  }

  const IconComponent = getCategoryIcon(project.category);
  const categoryColor = getCategoryColor(project.category);

  return (
    <div className="pt-16 md:pt-20 min-h-screen bg-[#0a0a0f]" data-testid="portfolio-detail-page">
      {/* Back Button */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <Link 
          to="/portfolyo" 
          className="inline-flex items-center gap-2 text-[#9ca3af] hover:text-[#00d4aa] transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Portföye Dön
        </Link>
      </div>

      {/* Hero Section */}
      <section className="pb-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-start">
            {/* Image */}
            <div className="relative">
              <div className="aspect-video bg-gradient-to-br from-[#16161f] to-[#0a0a0f] rounded-2xl overflow-hidden border border-[#2a2a3a]">
                {project.image_url ? (
                  <img 
                    src={project.image_url} 
                    alt={project.title}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <IconComponent 
                      className="w-24 h-24 opacity-20" 
                      style={{ color: categoryColor }}
                    />
                  </div>
                )}
              </div>
              
              {project.is_featured && (
                <div className="absolute top-4 left-4 px-4 py-2 bg-[#00d4aa] text-[#0a0a0f] text-sm font-semibold rounded-full">
                  Öne Çıkan Proje
                </div>
              )}
            </div>

            {/* Info */}
            <div>
              <div className="flex items-center gap-3 mb-4">
                <span 
                  className="px-4 py-2 rounded-full text-sm font-medium flex items-center gap-2"
                  style={{ 
                    backgroundColor: `${categoryColor}15`,
                    color: categoryColor
                  }}
                >
                  <IconComponent className="w-4 h-4" />
                  {getCategoryLabel(project.category)}
                </span>
              </div>

              <h1 className="text-3xl sm:text-4xl font-bold text-[#f0f0f5] mb-4">
                {project.title}
              </h1>

              <p className="text-[#9ca3af] text-lg leading-relaxed mb-6">
                {project.description}
              </p>

              {/* Meta Info */}
              <div className="grid sm:grid-cols-2 gap-4 mb-8">
                {project.client_name && (
                  <div className="flex items-center gap-3 p-4 bg-[#16161f] border border-[#2a2a3a] rounded-xl">
                    <Building2 className="w-5 h-5 text-[#00d4aa]" />
                    <div>
                      <p className="text-[#9ca3af] text-xs">Müşteri</p>
                      <p className="text-[#f0f0f5] font-medium">{project.client_name}</p>
                    </div>
                  </div>
                )}
                <div className="flex items-center gap-3 p-4 bg-[#16161f] border border-[#2a2a3a] rounded-xl">
                  <Layers className="w-5 h-5 text-[#0ea5e9]" />
                  <div>
                    <p className="text-[#9ca3af] text-xs">Kategori</p>
                    <p className="text-[#f0f0f5] font-medium">{getCategoryLabel(project.category)}</p>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex flex-col sm:flex-row gap-4">
                <Button
                  onClick={handleOrderClick}
                  className="flex-1 bg-gradient-to-r from-[#00d4aa] to-[#0ea5e9] hover:opacity-90 text-[#0a0a0f] font-semibold py-6"
                  data-testid="order-similar-btn"
                >
                  <ShoppingCart className="w-5 h-5 mr-2" />
                  Benzer Proje Sipariş Ver
                </Button>
                
                {project.project_url && (
                  <a
                    href={project.project_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex-1"
                  >
                    <Button
                      variant="outline"
                      className="w-full border-[#2a2a3a] text-[#f0f0f5] hover:border-[#00d4aa] hover:text-[#00d4aa] py-6"
                    >
                      <ExternalLink className="w-5 h-5 mr-2" />
                      Canlı Demo
                    </Button>
                  </a>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Technologies */}
      {project.technologies && project.technologies.length > 0 && (
        <section className="py-12 bg-[#12121a]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-2xl font-bold text-[#f0f0f5] mb-6 flex items-center gap-3">
              <span className="text-[#00d4aa] font-mono text-sm">{'// '}</span>
              Kullanılan Teknolojiler
            </h2>
            
            <div className="grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {project.technologies.map((tech, idx) => (
                <div 
                  key={idx}
                  className="flex items-center gap-3 p-4 bg-[#16161f] border border-[#2a2a3a] rounded-xl hover:border-[#00d4aa] transition-colors"
                >
                  <CheckCircle2 className="w-5 h-5 text-[#00d4aa]" />
                  <span className="text-[#f0f0f5] font-medium">{tech}</span>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Features/Capabilities */}
      <section className="py-12 bg-[#0a0a0f]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl font-bold text-[#f0f0f5] mb-6 flex items-center gap-3">
            <span className="text-[#00d4aa] font-mono text-sm">{'// '}</span>
            Proje Özellikleri
          </h2>
          
          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-[#16161f] border border-[#2a2a3a] rounded-xl p-6">
              <h3 className="text-lg font-semibold text-[#f0f0f5] mb-4">Proje Detayları</h3>
              <p className="text-[#9ca3af] leading-relaxed">
                {project.description}
              </p>
            </div>
            
            <div className="bg-[#16161f] border border-[#2a2a3a] rounded-xl p-6">
              <h3 className="text-lg font-semibold text-[#f0f0f5] mb-4">Neden Bu Proje?</h3>
              <ul className="space-y-3">
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-[#00d4aa] mt-0.5 flex-shrink-0" />
                  <span className="text-[#9ca3af]">Modern ve kullanıcı dostu arayüz</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-[#00d4aa] mt-0.5 flex-shrink-0" />
                  <span className="text-[#9ca3af]">Ölçeklenebilir ve güvenli altyapı</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-[#00d4aa] mt-0.5 flex-shrink-0" />
                  <span className="text-[#9ca3af]">Performans odaklı geliştirme</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-[#00d4aa] mt-0.5 flex-shrink-0" />
                  <span className="text-[#9ca3af]">Sürekli destek ve bakım</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-[#12121a]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl sm:text-3xl font-bold text-[#f0f0f5] mb-4">
            Benzer Bir Proje mi İstiyorsunuz?
          </h2>
          <p className="text-[#9ca3af] mb-8">
            Bu projeye benzer veya tamamen özelleştirilmiş bir çözüm için hemen iletişime geçin.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              onClick={handleOrderClick}
              className="bg-[#00d4aa] hover:bg-[#00b894] text-[#0a0a0f] font-semibold px-8 py-6"
            >
              <ShoppingCart className="w-5 h-5 mr-2" />
              Sipariş Ver
            </Button>
            <Link to="/iletisim">
              <Button
                variant="outline"
                className="border-[#2a2a3a] text-[#f0f0f5] hover:border-[#00d4aa] px-8 py-6"
              >
                İletişime Geç
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Order Dialog */}
      <Dialog open={orderDialog} onOpenChange={setOrderDialog}>
        <DialogContent className="bg-[#16161f] border-[#2a2a3a] text-[#f0f0f5] max-w-lg" aria-describedby="order-dialog-description">
          <DialogHeader>
            <DialogTitle>Benzer Proje Sipariş Ver</DialogTitle>
            <p id="order-dialog-description" className="text-sm text-[#9ca3af]">
              Bu projeye benzer bir proje talebi oluşturun
            </p>
          </DialogHeader>

          <div className="space-y-4">
            <div className="bg-[#0a0a0f] rounded-lg p-4">
              <h3 className="font-semibold text-[#f0f0f5] mb-1">{project.title} benzeri proje</h3>
              <p className="text-[#9ca3af] text-sm">Kategori: {getCategoryLabel(project.category)}</p>
            </div>

            <div className="space-y-2">
              <Label>Proje Detaylarınız</Label>
              <Textarea
                value={orderDescription}
                onChange={(e) => setOrderDescription(e.target.value)}
                placeholder="Bu projeye benzer ne tür bir proje istiyorsunuz? Özel istekleriniz neler?"
                rows={5}
                className="bg-[#0a0a0f] border-[#2a2a3a] resize-none"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="ghost" onClick={() => setOrderDialog(false)} className="text-[#9ca3af]">
              İptal
            </Button>
            <Button 
              onClick={handleOrderSubmit} 
              disabled={ordering}
              className="bg-[#00d4aa] hover:bg-[#00b894] text-[#0a0a0f]"
            >
              {ordering ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Oluşturuluyor...</>
              ) : (
                <><ShoppingCart className="w-4 h-4 mr-2" />Siparişi Gönder</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PortfolioDetailPage;
